import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import { chartTimePeriod } from '../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { clearActivePin, setActivePin } from '../../../../redux/slices/ReportSlice';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import report from '../../../../utils/api/reports';

interface ChartDataProperties {
  label: string;
  completed: number;
  canceled: number;
  [key: string]: string | number; // Add this to allow dynamic properties
}

/**
 *  Energy report duration chart
 */
function BookingReportDurationChart() {
  const { t } = useTranslation('report');
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);

  /**
   * Function to show custom tooltip
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;

      return (
        <div className="energy-report-tooltip">
          <p className="label">{data?.label}</p>
          <div className="tooltip-values">
            <p style={{ color: '#2E9E3A' }}>{t('completed')}: {data?.completed || 0} </p>
            <p style={{ color: '#E41010' }}>{t('canceled')}: {data?.canceled || 0} </p>
          </div>
        </div>
      );
    }

    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await report.fetchReportchart({
        timePeriod: range,
        reportType: 'scheduleBookingCompletedVsCanceled',
        accountCustomId: reportsData?.accountId,
      });

      if (response.status === 200) {
        let transformedData: ChartDataProperties[];

        switch (range) {
          case 'last12months':
            transformedData = fillMissingMonthsGeneric(response.data).map((item) => ({
              label: item.label,
              completed: (item as any).completed || 0,
              canceled: (item as any).canceled || 0,
            }));
            break;
          case 'lastMonth':
            transformedData = fillMissingLastNDaysGeneric(response.data, 30).map((item) => ({
              label: item.label,
              completed: (item as any).completed || 0,
              canceled: (item as any).canceled || 0,
            }));
            break;
          case 'last7days':
            transformedData = fillMissingLastNDaysGeneric(response.data, 7).map((item) => ({
              label: item.label,
              completed: (item as any).completed || 0,
              canceled: (item as any).canceled || 0,
            }));
            break;
          case 'last24hours':
            transformedData = fillMissingLast24HrsGeneric(response.data).map((item) => ({
              label: item.label,
              completed: (item as any).completed || 0,
              canceled: (item as any).canceled || 0,
            }));
            break;
          default:
            transformedData = [];
        }

        setBarGraphData(transformedData);
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };

  /**
   * If clicking the already active pin, deactivate it
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  return (
    <div className="energy-report-chart">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin energy consumption trend based on revenue tiers"
              className={`pin-button ${reportsData.activePinName.includes('scheduleBookingCompletedVsCanceled') ? 'active' : ''}`}
              onClick={() => handleClick('scheduleBookingCompletedVsCanceled')}>
              {reportsData.activePinName.includes('scheduleBookingCompletedVsCanceled') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>
            {t('bookings')} {t('completed')} {t('vs')} {t('cancelled')}{' '}
          </div>
          <CustomToggleButton
            labels={chartTimePeriod.map((duration) => t(duration.label))}
            onToggle={(index) =>
              fetchPowerConsumedReport(
                chartTimePeriod[index]?.value || 'last12months',
              )
            }
            defaultActiveIndex={0}
            type="chart"
          />
        </div>

        <div className="energy-report-container">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barGraphData} barCategoryGap={25}>
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                fontSize={12}
                stroke="#535353"
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Bar dataKey="completed" stackId="a" barSize={26} fill="#2E9E3A" />
              <Bar dataKey="canceled" stackId="a" barSize={26} fill="#E41010" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="energy-report-chart-label">
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#2E9E3A' }} />
          <p>{t('completed')}</p>
        </div>
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#E41010' }} />
          <p>{t('canceled')}</p>
        </div>
      </div>
    </div>
  );
}

export default BookingReportDurationChart;
